import React, { useState, useEffect, useRef, useCallback } from 'react';
import baseURL from '../../Components/url';
import NavbarWorker from '../../Components/Worker/NavbarWorker/NavbarWorker';
import moneda from '../../Components/moneda';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import './WorkerPedidosWeb.css';

export default function WorkerPedidosWeb() {
    const [pedidos, setPedidos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const pedidosIdsRef = useRef(new Set());
    const primeraCargarRef = useRef(true);

    const sonarNotificacion = useCallback(() => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const notas = [830, 1050, 1320];
            notas.forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.frequency.value = freq;
                osc.type = 'sine';
                gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.15);
                gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.15 + 0.3);
                osc.start(ctx.currentTime + i * 0.15);
                osc.stop(ctx.currentTime + i * 0.15 + 0.3);
            });
        } catch (e) { }
    }, []);

    const cargarPedidos = useCallback(() => {
        fetch(`${baseURL}/pedidoGet.php`)
            .then(r => r.json())
            .then(data => {
                const todos = data.pedidos || [];
                const webPedidos = todos.filter(p => !p.idMesa || p.idMesa === '0' || p.idMesa === 0);

                if (primeraCargarRef.current) {
                    pedidosIdsRef.current = new Set(webPedidos.map(p => String(p.idPedido)));
                    primeraCargarRef.current = false;
                } else {
                    const nuevos = webPedidos.filter(p => !pedidosIdsRef.current.has(String(p.idPedido)));
                    if (nuevos.length > 0) {
                        sonarNotificacion();
                        nuevos.forEach(p => {
                            toast.info(`Nuevo pedido #${p.idPedido} de ${p.nombre}`, { autoClose: 5000 });
                        });
                        pedidosIdsRef.current = new Set(webPedidos.map(p => String(p.idPedido)));
                    }
                }

                setPedidos(webPedidos);
                setLoading(false);
            })
            .catch(err => { console.error(err); setLoading(false); });
    }, [sonarNotificacion]);

    useEffect(() => {
        cargarPedidos();
        const intervalo = setInterval(cargarPedidos, 60000);
        return () => clearInterval(intervalo);
    }, [cargarPedidos]);

    const estadosActivos = ['Pendiente', 'En preparación', 'Listo'];

    const getEstadoClass = (estado) => {
        switch (estado) {
            case 'Pendiente': return 'estadoPendiente';
            case 'En preparación': return 'estadoPreparacion';
            case 'Listo': return 'estadoListo';
            case 'Entregado': return 'estadoEntregado';
            case 'Cancelado': return 'estadoCancelado';
            default: return '';
        }
    };

    const getEstadoPagoClass = (estadoPago) => {
        switch (estadoPago) {
            case 'Pagado': return 'estadoPagoPagado';
            case 'Cliente confiable': return 'estadoPagoConfiable';
            case 'Sin contactar': return 'estadoPagoSinContactar';
            default: return 'estadoPagoEspera';
        }
    };

    const ejecutarCambioEstado = async (idPedido, nuevoEstado) => {
        try {
            const response = await fetch(`${baseURL}/pedidoPut.php?idPedido=${idPedido}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estado: nuevoEstado }),
            });
            const data = await response.json();
            if (data.mensaje) {
                toast.success(data.mensaje);
                cargarPedidos();
            } else {
                toast.error(data.error || 'Error al cambiar estado');
            }
        } catch (err) {
            toast.error('Error de conexión');
        }
    };

    const cambiarEstadoPago = async (idPedido, nuevoEstadoPago) => {
        if (nuevoEstadoPago === 'Pagado') {
            const result = await Swal.fire({
                title: '¿Confirmar pago recibido?',
                text: 'Una vez confirmado el pago, no se podrá cambiar el estado de pago.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#2e7d32',
                cancelButtonColor: '#999',
                confirmButtonText: 'Sí, pago confirmado',
                cancelButtonText: 'Cancelar',
            });
            if (!result.isConfirmed) return;
        } else if (nuevoEstadoPago === 'Cliente confiable') {
            const result = await Swal.fire({
                title: '¿Marcar como cliente confiable?',
                html: '<strong style="color:#d33;">Al seleccionar esta opción, la responsabilidad del pago recae completamente sobre el trabajador.</strong><br><br>Una vez seleccionado, no se podrá cambiar el estado de pago.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e67e22',
                cancelButtonColor: '#999',
                confirmButtonText: 'Sí, asumo la responsabilidad',
                cancelButtonText: 'Cancelar',
            });
            if (!result.isConfirmed) return;
        } else if (nuevoEstadoPago === 'Espera de pago') {
            const result = await Swal.fire({
                title: '¿Marcar como espera de pago?',
                text: 'Una vez seleccionado, no se podrá cambiar el estado de pago. Los estados del pedido quedarán bloqueados hasta que se gestione nuevamente.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e65100',
                cancelButtonColor: '#999',
                confirmButtonText: 'Sí, confirmar',
                cancelButtonText: 'Cancelar',
            });
            if (!result.isConfirmed) return;
        } else if (nuevoEstadoPago === 'Sin contactar') {
            const result = await Swal.fire({
                title: '¿No se pudo contactar al cliente?',
                text: 'El pedido se marcará como sin contactar. No se podrá cambiar el estado de pago.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#8e44ad',
                cancelButtonColor: '#999',
                confirmButtonText: 'Sí, sin contactar',
                cancelButtonText: 'Cancelar',
            });
            if (!result.isConfirmed) return;
        }

        try {
            const response = await fetch(`${baseURL}/pedidoPut.php?idPedido=${idPedido}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estadoPago: nuevoEstadoPago }),
            });
            const data = await response.json();
            if (data.mensaje) {
                toast.success(data.mensaje);
                if (nuevoEstadoPago === 'Sin contactar') {
                    await ejecutarCambioEstado(idPedido, 'Cancelado');
                } else {
                    cargarPedidos();
                }
            } else {
                toast.error(data.error || 'Error al cambiar estado de pago');
            }
        } catch (err) {
            toast.error('Error de conexión');
        }
    };

    const enviarWhatsappListo = (pedido) => {
        const productos = typeof pedido.productos === 'string'
            ? JSON.parse(pedido.productos)
            : pedido.productos;

        const detalleProductos = productos.map(p =>
            `  - ${p.cantidad}x ${p.titulo}${p.item ? ' (' + p.item + ')' : ''}`
        ).join('\n');

        const totalFormateado = String(Math.round(Number(pedido.total))).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        const entregaTexto = pedido.entrega === 'Retiro personalmente' ? 'Recoger en tienda' : `Domicilio: ${pedido.entrega}`;
        const pagoTexto = pedido.pago === 'efectivo' ? 'Efectivo' : pedido.pago === 'transferencia' ? 'Transferencia bancaria' : pedido.pago;

        let message = `Hola ${pedido.nombre}, tu pedido #${pedido.idPedido} esta *LISTO*\n\n`;
        message += `Productos:\n${detalleProductos}\n\n`;
        message += `Total: ${moneda} ${totalFormateado}\n`;
        message += `Entrega: ${entregaTexto}\n`;
        if (pagoTexto && pagoTexto !== 'Por definir') message += `Pago: ${pagoTexto}\n`;
        message += `\nGracias por tu compra!`;

        const whatsappUrl = `https://api.whatsapp.com/send?phone=${pedido.telefono}&text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    const cambiarEstado = (idPedido, nuevoEstado) => {
        const pedido = pedidos.find(p => String(p.idPedido) === String(idPedido));
        const estadoPago = pedido?.estadoPago || null;
        const pagoFijado = estadoPago === 'Pagado' || estadoPago === 'Cliente confiable' || estadoPago === 'Sin contactar';

        if (!pagoFijado) {
            Swal.fire({
                title: 'Pago no confirmado',
                text: 'Debe confirmar el pago (Pagado o Cliente confiable) antes de cambiar el estado del pedido.',
                icon: 'error',
                confirmButtonColor: '#999',
                confirmButtonText: 'Entendido',
            });
            return;
        }

        if (nuevoEstado === 'Listo') {
            Swal.fire({
                title: '¿Marcar como listo?',
                text: 'Se enviará un mensaje de WhatsApp al cliente notificando que su pedido está listo.',
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#2e7d32',
                cancelButtonColor: '#999',
                confirmButtonText: 'Sí, está listo',
                cancelButtonText: 'Volver',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await ejecutarCambioEstado(idPedido, nuevoEstado);
                    if (pedido?.telefono) {
                        enviarWhatsappListo(pedido);
                    }
                }
            });
            return;
        }
        if (nuevoEstado === 'Entregado') {
            Swal.fire({
                title: '¿Confirmar entrega?',
                text: 'Una vez marcado como entregado no se podrá cambiar el estado.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#27ae60',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, fue entregado',
                cancelButtonText: 'Volver',
            }).then((result) => {
                if (result.isConfirmed) ejecutarCambioEstado(idPedido, nuevoEstado);
            });
        } else if (nuevoEstado === 'Cancelado') {
            Swal.fire({
                title: '¿Cancelar pedido?',
                text: 'Una vez cancelado no se podrá cambiar el estado del pedido.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#999',
                confirmButtonText: 'Sí, cancelar pedido',
                cancelButtonText: 'Volver',
            }).then((result) => {
                if (result.isConfirmed) ejecutarCambioEstado(idPedido, nuevoEstado);
            });
        } else {
            ejecutarCambioEstado(idPedido, nuevoEstado);
        }
    };

    const estados = ['Pendiente', 'En preparación', 'Listo', 'Entregado', 'Cancelado'];
    const estadosPago = ['Pagado', 'Espera de pago', 'Cliente confiable', 'Sin contactar'];

    const filtrarPedidos = (lista) => {
        if (!busqueda.trim()) return lista;
        const q = busqueda.trim().toLowerCase();
        return lista.filter(p =>
            String(p.idPedido).includes(q) ||
            (p.telefono && p.telefono.toLowerCase().includes(q)) ||
            (p.nombre && p.nombre.toLowerCase().includes(q))
        );
    };

    const pedidosActivos = filtrarPedidos(pedidos.filter(p => estadosActivos.includes(p.estado)));
    const pedidosFinalizados = filtrarPedidos(pedidos.filter(p => !estadosActivos.includes(p.estado)));

    const generarMensajeWhatsapp = (pedido) => {
        const esDomicilio = pedido.entrega && pedido.entrega !== 'Retiro personalmente' && pedido.entrega !== 'Retiro en personalmente';
        const esEfectivo = pedido.pago === 'efectivo';
        const esTransferencia = pedido.pago === 'transferencia';

        let msg = `Hola ${pedido.nombre}, gracias por tu pedido #${pedido.idPedido}.\n\n`;

        if (esEfectivo) {
            msg += `Para preparar tu pedido necesitamos que nos suministres la siguiente información:\n\n`;
            msg += `- Nombre completo\n`;
            msg += `- Dirección exacta\n`;
            msg += `- Número de teléfono con WhatsApp\n`;
            msg += `- Cédula de identidad\n\n`;
        } else if (esTransferencia) {
            msg += `Para verificar tu pago y comenzar a preparar tu pedido, necesitamos que nos envíes el *comprobante de transferencia*.\n\n`;
        }

        msg += `Quedamos atentos!`;

        return msg;
    };

    const renderPedido = (pedido) => {
        const productos = typeof pedido.productos === 'string'
            ? JSON.parse(pedido.productos)
            : pedido.productos;

        const estadoPago = pedido.estadoPago || null;
        const pagoFijado = estadoPago === 'Pagado' || estadoPago === 'Cliente confiable' || estadoPago === 'Sin contactar';
        const bloqueado = !pagoFijado;

        return (
            <div key={pedido.idPedido} className='webPedidoCard'>
                <div className='webPedidoHeader'>
                    <span>Pedido #{pedido.idPedido}</span>
                    <span className={getEstadoClass(pedido.estado)}>{pedido.estado}</span>
                </div>

                <div className='webPedidoCliente'>
                    {pedido.nombre && <p><strong>Cliente:</strong> {pedido.nombre}</p>}
                    {pedido.telefono && (
                        <p className='webClienteTelefono'>
                            <strong>Teléfono:</strong> {pedido.telefono}
                            <a
                                href={`https://api.whatsapp.com/send?phone=${pedido.telefono}&text=${encodeURIComponent(generarMensajeWhatsapp(pedido))}`}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='webWhatsappBtn'
                                title='Contactar por WhatsApp'
                            >
                                WhatsApp
                            </a>
                        </p>
                    )}
                    {pedido.email && <p><strong>Email:</strong> {pedido.email}</p>}
                    {pedido.entrega && <p><strong>Entrega:</strong> {pedido.entrega}</p>}
                    {pedido.pago && pedido.pago !== 'Por definir' && <p><strong>Pago:</strong> {pedido.pago === 'efectivo' ? 'Efectivo' : pedido.pago === 'transferencia' ? 'Transferencia' : pedido.pago}</p>}
                </div>

                <ul className='webPedidoProductos'>
                    {productos.map((prod, i) => (
                        <li key={i}>
                            {prod.cantidad}x {prod.titulo}
                            {prod.item && <span> - {prod.item}</span>}
                            {' - '}{moneda} {String(Math.round(prod.precio * prod.cantidad)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                            {prod.nota && <span className='webProdNota'>({prod.nota})</span>}
                        </li>
                    ))}
                </ul>

                <div className='webPedidoTotal'>
                    Total: {moneda} {String(Math.round(Number(pedido.total))).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                </div>

                {pedido.nota && <p className='webPedidoNota'>Nota: {pedido.nota}</p>}

                {/* Estado de pago */}
                {pedido.estado !== 'Entregado' && pedido.estado !== 'Cancelado' && (
                    <div className='webPedidoPagoSection'>
                        <span className='webPedidoPagoLabel'>Estado de pago:</span>
                        <div className='webPedidoPagoBtns'>
                            {estadosPago.map(ep => (
                                <button
                                    key={ep}
                                    className={`webPagoBtn ${estadoPago === ep ? 'webPagoBtnActivo' : ''}`}
                                    onClick={() => cambiarEstadoPago(pedido.idPedido, ep)}
                                    disabled={pagoFijado}
                                >
                                    {ep}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {pedido.estado === 'Entregado' ? (
                    <div className='webPedidoEntregado'>Pedido entregado</div>
                ) : pedido.estado === 'Cancelado' ? (
                    <div className='webPedidoCancelado'>Pedido cancelado</div>
                ) : (
                    <div className={`webPedidoAcciones ${bloqueado ? 'webAccionesBloqueadas' : ''}`}>
                        {estados.map(est => (
                            <button
                                key={est}
                                className={`webEstadoBtn ${pedido.estado === est ? 'webEstadoBtnActivo' : ''}`}
                                onClick={() => cambiarEstado(pedido.idPedido, est)}
                                disabled={pedido.estado === est}
                            >
                                {est}
                            </button>
                        ))}
                        {bloqueado && <p className='webBloqueadoMsg'>Confirme el pago para cambiar el estado</p>}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className='workerContainer'>
            <NavbarWorker />
            <ToastContainer />
            <div className='webPedidosContent'>
                <h2>Pedidos Página Web</h2>
                <div className='webBuscadorRow'>
                    <input
                        type='text'
                        className='webBuscadorInput'
                        placeholder='Buscar por # pedido, teléfono o nombre...'
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                    />
                    <button className='refreshBtn' onClick={cargarPedidos}>
                        Actualizar
                    </button>
                </div>

                {loading ? (
                    <p>Cargando pedidos...</p>
                ) : pedidos.length === 0 ? (
                    <p className='noPedidos'>No hay pedidos desde la página web</p>
                ) : (
                    <>
                        <div className='webPedidosGrid'>
                            {pedidosActivos.map(renderPedido)}
                        </div>
                        {pedidosActivos.length > 0 && pedidosFinalizados.length > 0 && (
                            <div className='mesasDivisor'>
                                <span>Entregados / Cancelados</span>
                            </div>
                        )}
                        {pedidosFinalizados.length > 0 && (
                            <div className='webPedidosGrid'>
                                {pedidosFinalizados.map(p => (
                                    <div key={p.idPedido} className='webPedidoCardFinalizado'>
                                        {renderPedido(p)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
